/**
 * Gets the value for the specified cookie if present, otherwise returns null.
 *
 * @param name
 * @returns {string|null}
 */
function getCookie(name) {
    const nameWithSeparator = name + "=";
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();

        if (cookie.indexOf(nameWithSeparator) === 0) {
            return cookie.substring(nameWithSeparator.length, cookie.length);
        }
    }

    return null;
}

/**
 * On any page that loads GNav, it creates a <script> element containing various useful details,
 * including but not limited to:
 * - If the current page is an Employer page (isEmployer)
 * - If the user is on mobile (isMobile)
 * - If the user is logged in (isLoggedIn)
 * - The current language (language)
 * - The current country (country)
 *
 * @returns {object|null}
 */
function getGnavConfig() {
    const gnavConfigElement = document.getElementById("_indeed_gnav_config");

    if (gnavConfigElement !== null) {
        const gnavConfig = JSON.parse(gnavConfigElement.textContent);
        return gnavConfig;
    }

    return null;
}

/**
 * Determines if the user is logged in based on GNav (if present),
 * otherwise the existence of session auth cookies (SHOE or SOCK).
 *
 * The actual values are not relevant since they're an obfuscated value.
 *
 * Note: SOCK is only added for HTTPS requests.
 *
 * @returns {boolean}
 */
function isUserLoggedIn() {
    const gnavConfig = getGnavConfig();

    if (gnavConfig !== null && gnavConfig.hasOwnProperty("isLoggedIn")) {
        return gnavConfig.isLoggedIn;
    }

    return getCookie("SHOE") !== null || getCookie("SOCK") !== null;
}

/**
 * Determines if the user is a returning user.
 * The existence of the ADV means that the user has either visited the dashboard or the job posting funnel.
 * Its value will be 1 if present.
 * The cookie is not set by Post-Press, Tophat, or Allspark.
 *
 * @returns {boolean}
 */
function isReturningUser() {
    return getCookie("ADV") === "1";
}

/**
 * Determines if the user is new to the site based on session auth cookies and an advertiser tracking cookie.
 *
 * @returns {boolean}
 */
function isNewUser() {
    return !(isUserLoggedIn() || isReturningUser());
}

/**
 * Performs a best effort attempt at determining the current locale
 * by checking the GNav config (if present), the page wrap class (if present and in a valid format),
 * otherwise defaulting to en_US.
 *
 * Other possible methods include:
 * - Checking the hl and co query params (this is most likely already more-or-less handled in the GNav and page wrap methods)
 * - Checking the lang attribute on the <html> element
 *
 * @returns {{ hl: string, co: string }}
 */
function getLocale() {
    const gnavConfig = getGnavConfig();

    if (gnavConfig !== null && gnavConfig.hasOwnProperty("language")) {
        // Having the language is sufficient for at least rendering the destination page in the correct language.
        return { hl: gnavConfig.language, co: gnavConfig.country || "US" };
    }

    // Most WordPress pages have a pageWrap div that has the locale as a class.
    const pageWrapElement = document.getElementById("pageWrap");
    if (pageWrapElement && pageWrapElement.className.trim() !== "") {
        const locale = pageWrapElement.className.split(" ")[0]; // The first element is the locale if present.
        const twoOrFourLetterLocaleRegex = /^(([a-zA-Z]{2}_[a-zA-Z]{2})|[a-zA-Z]{2})$/;

        if (twoOrFourLetterLocaleRegex.test(locale)) {
            const hl = locale.split("_")[0];
            const co = locale.split("_")[1] || "US";

            return { hl: hl, co: co };
        }
    }

    return { hl: "en", co: "US" };
}

/**
 * Detects if we're in a production environment.
 * This is used to determine which hostname to use.
 *
 * @returns {boolean}
 */
function isProductionEnvironment() {
    const gnavConfig = getGnavConfig();

    if (gnavConfig !== null) {
        return !gnavConfig.isPreproduction;
    }

    const host = window.location.hostname;

    return !host.includes(".qa.indeed.net");
}

/**
 * Sets up the behavior to hide and reset the modal upon clicking the 'X' button or clicking the dark background overlay.
 */
function setupModalCloseListeners() {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");

    [].forEach.call(nopassModalWrapper.querySelectorAll(".nopass-close-button, .nopass-modal-overlay"), function (el) {
        el.addEventListener("click", function () {
            nopassModalWrapper.classList.add("hide-nopass");

            const companyName = document.getElementById("companyname");
            companyName.value = "";
            companyName.parentElement.classList.remove("nopass-text-input-wrapper--has-error");

            const email = document.getElementById("email");
            email.value = "";
            email.parentElement.classList.remove("nopass-text-input-wrapper--has-error");
            document.getElementById("label-no-companyname-error") && document.getElementById("label-no-companyname-error").classList.add("hide-nopass");
            document.getElementById("label-no-email-error") && document.getElementById("label-no-email-error").classList.add("hide-nopass");
            document.getElementById("label-invalid-email-error") && document.getElementById("label-invalid-email-error").classList.add("hide-nopass");

            clearAutocomplete();
        });
    });
}

/**
 * Trap the focus within the modal so users can't use keyboard navigation to change focus
 * to elements on the page while the modal is still up.
 */
function setupModalFocusTrap() {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    const focusableElements = nopassModalWrapper.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    nopassModalWrapper.addEventListener("keydown", function(e) {
        if (e.key === "Tab") {
            if (e.shiftKey) { // Shift + tab for going to the previous element.
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus();
                    e.preventDefault();
                }
            }
        }
    });
}

/**
 * Updates the hl and en query parameters from the default en_US in various links and attributes in the modal.
 *
 * @param hl
 * @param co
 */
function updateLocalesInLinksAndAttributes(hl, co) {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    [].forEach.call(nopassModalWrapper.querySelectorAll("#nopass-tos-link, #nopass-cookie-policy-link, #nopass-privacy-policy-link"), function (el) {
        el.href = el.href.replace("en", hl);
    });

    // Updates the hidden hl and co form fields.
    nopassModalWrapper.querySelector('[name="hl"]').value = hl;
    nopassModalWrapper.querySelector('[name="co"]').value = co;

    nopassModalWrapper.querySelector("#nopass-sign-in-link").href +=
        "?hl=" + hl +
        "&co=" + co +
        "&continue=" + encodeURIComponent(location.protocol + location.hostname + location.pathname);
}

/**
 * Builds the base 'continue' URL with various tracking parameters if present. Also includes the hash.
 *
 * @param hrefComponents
 * @param hl
 * @param co
 * @returns {string}
 */
function buildBaseContinueUrlWithTrackingParams(hrefComponents, hl, co) {
    const fromParam = hrefComponents.from !== null
        ? "&from=" + hrefComponents.from
        : "";
    const isidParam = hrefComponents.isid !== null
        ? "&isid=" + hrefComponents.isid
        : "";
    const ikwParam = hrefComponents.ikw !== null
        ? "&ikw=" + hrefComponents.ikw
        : "";

    const continueUrlWithTrackingParams = hrefComponents.baseContinueUrl +
        '?hl=' + hl +
        "&co=" + co +
        fromParam +
        isidParam +
        ikwParam +
        "&trafficTk=" + getCookie("CTK") +
        "&sid=" + location.hostname +
        "&kw=" + encodeURIComponent(location.pathname);

    return continueUrlWithTrackingParams;
}

/**
 * Builds the final 'continue' URL with the "launch time" (jstm) and the 'prepopulateWith' parameter
 * to pre-fill out the job posting funnel company name field if needed.
 *
 * @param baseContinueUrlWithTrackingParams
 * @param companyName
 * @returns {string}
 */
function buildFinalContinueUrlWithJobPostingFunnelParamsIfNeeded(baseContinueUrlWithTrackingParams, companyName) {
    const url = new URL(baseContinueUrlWithTrackingParams);

    let prepopulateWithParam = "";
    if (["/o/p", "/p", "/p/post-job", "/j/post-job"].some(jobPostingFunnelUrl => url.pathname === jobPostingFunnelUrl)) {
        prepopulateWithParam = "&prepopulateWith=" + encodeURIComponent('{"advertiserCompany":"' + companyName + '"}');
    }

    const finalContinueUrlWithQueryParams = baseContinueUrlWithTrackingParams +
        "&jstm=" + new Date().getTime().toString() +
        prepopulateWithParam;

    return finalContinueUrlWithQueryParams;
}

/**
 * Removes the autocomplete suggestion list.
 */
function clearAutocomplete() {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    const autoComplete = nopassModalWrapper.querySelector(".nopass-autocomplete");
    nopassModalWrapper.querySelector(".nopass-autocomplete-list") &&
    (autoComplete.classList.remove("is-open"),
        nopassModalWrapper.querySelector(".nopass-autocomplete-list").remove(),
        nopassModalWrapper.querySelector(".nopass-autocomplete-aria-results-available-wrapper").remove());
    window.currentAutocompleteOptionIndex = -1;
}

/**
 * Updates the index so we know which option to highlight in gray and fill the company name field with.
 *
 * @param isDownKey
 */
function updateAutocompleteOptionIndexForKeyboardNavigation(companyNames, isDownKey) {
    const indexOffset = isDownKey
        ? 1
        : -1;

    // Circular list traversal.
    if (window.currentAutocompleteOptionIndex + indexOffset >= companyNames.length) {
        window.currentAutocompleteOptionIndex = -1;
    } else if (window.currentAutocompleteOptionIndex + indexOffset < -1) {
        window.currentAutocompleteOptionIndex = companyNames.length - 1;
    } else {
        window.currentAutocompleteOptionIndex += indexOffset;
    }
}

/**
 * Sets up circular keyboard navigation and selection of the autocomplete suggestion list.
 */
function setupAutocompleteKeyboardNavigation() {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    const autoComplete = nopassModalWrapper.querySelector(".nopass-autocomplete");
    const companyName = nopassModalWrapper.querySelector("#companyname");

    companyName.addEventListener("keydown", function (e) {
        const isDownKey = e.key === "ArrowDown";
        const isUpKey = e.key === "ArrowUp";
        const isEnterKey = e.key === "Enter";
        const isEscapeKey = e.key === "Escape";
        const isTabKey = e.key === "Tab";

        const companyNames = nopassModalWrapper.querySelectorAll(".nopass-autocomplete-option"); // This needs to be grabbed on each event or else it won't be up-to-date.

        if (companyName.value && isEscapeKey) { // Close out of the autocomplete and reset the field back to the user's input.
            companyName.value = window.currentCompanyNameUserInput;
            clearAutocomplete();
            return;
        } else if (isEnterKey || isTabKey) { // Close out of the autocomplete and accept whichever option is selected (if any).
            const currentlySelectedAutocompleteOption = nopassModalWrapper.querySelector(".nopass-autocomplete-option.has-focus");

            if (currentlySelectedAutocompleteOption) {
                window.currentCompanyNameUserInput = currentlySelectedAutocompleteOption.textContent.trim();
            }

            clearAutocomplete();
            return;
        } else if (!autoComplete.classList.contains("is-open") && (isDownKey || isUpKey)) { // There aren't any suggestions, so behave normally for up/down. Don't adjust the index in this case.
            return;
        } else if ((!companyNames || !companyNames.length) || (!isUpKey && !isDownKey)) { // Unsupported key or there aren't any suggestions.
            return;
        }

        e.preventDefault();

        updateAutocompleteOptionIndexForKeyboardNavigation(companyNames, isDownKey);

        [].forEach.call(nopassModalWrapper.querySelectorAll(".nopass-autocomplete-option.has-focus"), function (el) {
            el.classList.remove("has-focus");
        });

        if (window.currentAutocompleteOptionIndex === -1) {
            companyName.value = window.currentCompanyNameUserInput; // Reset the field value to the user input if the user has keyboard navigated through all the autocomplete options.
        } else if (window.currentAutocompleteOptionIndex >= 0) {
            const currentlySelectedAutocompleteOption = nopassModalWrapper.querySelectorAll(".nopass-autocomplete-option")[window.currentAutocompleteOptionIndex];
            currentlySelectedAutocompleteOption.classList.add("has-focus");
            companyName.value = currentlySelectedAutocompleteOption.textContent.trim();
        }
    });
}

/**
 * Sets up event listeners to query the Autocomplete service when a new company name is provided.
 * Each autocomplete option also gets an 'on click' event listener to update the form field.
 *
 * @param hl
 * @param co
 */
function setupAutocompleteOnChangeEventListeners(hl, co) {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    const autoComplete = nopassModalWrapper.querySelector(".nopass-autocomplete");
    const companyName = nopassModalWrapper.querySelector("#companyname");

    const employerSubdomain = isProductionEnvironment()
        ? "https://employers.indeed.com"
        : "https://employers.qa.indeed.net";

    ["input", "paste"].forEach(function (event) {
        companyName.addEventListener(event, function () {
            clearAutocomplete();

            if (!companyName.value) {
                companyName.parentElement.classList.add("nopass-text-input-wrapper--has-error");
                document.getElementById("label-no-companyname-error") && document.getElementById("label-no-companyname-error").classList.remove("hide-nopass");
            } else {
                companyName.parentElement.classList.remove("nopass-text-input-wrapper--has-error");
                document.getElementById("label-no-companyname-error") && document.getElementById("label-no-companyname-error").classList.add("hide-nopass");

                window.currentCompanyNameUserInput = companyName.value;

                let companyNames = []; // Modify this to test locally.

                fetch(
                    employerSubdomain + "/api/v2/utilities/companies/autocomplete?name=" +
                    encodeURI(companyName.value) +
                    "&locale=" +
                    hl + "-" + co,
                    {
                        method: "GET",
                        headers: {
                            Authorization:
                                "Basic c3Bhcms6NDQwNDY5NzFmYmE1NWRhZTNiN2FjMmU0YmUyMmU5NzFmYWFkNmRkOWNlMTE0MjNkMzA1ZDMyY2YwMjk4NGQ5ZA=="
                        }
                    }
                )
                    .then(function (data) {
                        return data.json();
                    })
                    .then(
                        function (data) {
                            companyNames = data.companyNames;
                        },
                        function (e) {
                            console.error("Something went wrong while fetching company names for autocompletion: ", e);
                        }
                    ).finally(() => {
                        clearAutocomplete();
                        if (companyNames.length) {
                            autoComplete.classList.add("is-open");

                            let list = '<ul class="nopass-autocomplete-list" role="listbox">';

                            companyNames.forEach(function (el) {
                                const item =
                                    '<li role="option" class="nopass-autocomplete-option">' +
                                    '  <span class="nopass-autocomplete-text">' +
                                    '    <span>' + el + '</span>' +
                                    '  </span>' +
                                    '</li>';
                                list += item;
                            });

                            list +=
                                '</ul>' +
                                '<div class="nopass-autocomplete-aria-results-available-wrapper">' +
                                '  <span aria-live="polite" aria-atomic="true" role="status" class="nopass-autocomplete-aria-results-available">' + companyNames.length + ' undefined</span>' +
                                '</div>';

                            autoComplete.insertAdjacentHTML("beforeend", list);

                            [].forEach.call(nopassModalWrapper.querySelectorAll(".nopass-autocomplete-option"), function (el) {
                                el.addEventListener("click", function () {
                                    companyName.value = el.textContent.trim();
                                    window.currentCompanyNameUserInput = companyName.value;
                                    clearAutocomplete();
                                });
                            });
                        }
                    }
                );
            }
        });
    });
}

/**
 * Sets up the Company Name form field to send requests to the
 * Utilities API autocomplete endpoint and update the field with results if the call was successful.
 *
 * @param hl
 * @param co
 */
function setupCompanyNameAutocomplete(hl, co) {
    window.currentAutocompleteOptionIndex = -1; // This is used to track which autocomplete option has focus.
    window.currentCompanyNameUserInput = ""; // This is used to track what the user input was so we can reset the field.

    setupAutocompleteKeyboardNavigation();
    setupAutocompleteOnChangeEventListeners(hl, co);
}

/**
 * Sets up email validation for the Email form field.
 * A red outline around the input field and an error message is displayed for invalid emails.
 */
function setupEmailValidationListener() {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    const email = nopassModalWrapper.querySelector("#email");

    ["input", "paste"].forEach(function (event) {
        email.addEventListener(event, function () {
            document.getElementById("label-no-email-error") && document.getElementById("label-no-email-error").classList.add("hide-nopass");
            document.getElementById("label-invalid-email-error") && document.getElementById("label-invalid-email-error").classList.add("hide-nopass");

            if (!email.value) {
                email.parentElement.classList.add("nopass-text-input-wrapper--has-error");
                document.getElementById("label-no-email-error") && document.getElementById("label-no-email-error").classList.remove("hide-nopass");
            } else if (
                // Note: This doesn't seem to be the general email regex defined in RFC 5322.
                !/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(
                    email.value
                )
            ) {
                email.parentElement.classList.add("nopass-text-input-wrapper--has-error");
                document.getElementById("label-invalid-email-error") && document.getElementById("label-invalid-email-error").classList.remove("hide-nopass");
            } else {
                email.parentElement.classList.remove("nopass-text-input-wrapper--has-error");
                document.getElementById("label-no-email-error") && document.getElementById("label-no-email-error").classList.add("hide-nopass");
                document.getElementById("label-invalid-email-error") && document.getElementById("label-invalid-email-error").classList.add("hide-nopass");
            }
        });
    });
}

/**
 * Toggles the 'Continue' button if both visible form fields are valid:
 * - Company Name: not empty
 * - Email: matches the valid email regex
 */
function enableSubmitButtonIfInputsAreValid() {
    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    const inputs = nopassModalWrapper.querySelectorAll(".nopass-text-input-control");
    [].forEach.call(inputs, function (input) {
        ["input", "paste"].forEach(function (event) {
            input.addEventListener(event, function () {
                if (
                    [].every.call(inputs, function (el) {
                        return el.value;
                    }) &&
                    !nopassModalWrapper.querySelectorAll(".nopass-text-input-wrapper--has-error").length
                )
                    nopassModalWrapper.querySelector("#continueButton").removeAttribute("disabled");
                else {
                    nopassModalWrapper.querySelector("#continueButton").setAttribute("disabled", true);
                }
            });
        });
    });
}

/**
 * Parses the href into its various components to be used to build the final 'continue' URL.
 *
 * @param href
 * @returns {{ isid: string, ikw: string, baseContinueUrl: string, from: string }}
 */
function parseHref(href) {
    const url = new URL(href);

    // Replace references with the production site with the QA version.
    // Otherwise Passport's NoPass endpoint /account/autoregister will 404 due to mismatched environments.
    const hostname = isProductionEnvironment()
        ? url.hostname
        : url.hostname.replace(".indeed.com", ".qa.indeed.net");

    const baseContinueUrl = url.protocol + "//" + hostname + url.pathname + url.hash; // Hash is the empty string, not null if not present.
    const from = url.searchParams.get("from");
    const isid = url.searchParams.get("isid");
    const ikw = url.searchParams.get("ikw");

    return { baseContinueUrl: baseContinueUrl, from: from, isid: isid, ikw: ikw };
}

/**
 * Sets up the behavior to build the final 'continue' URL based on which CTA is clicked.
 * The value is only generated upon form submit.
 * The event listeners are added per CTA so we maintain their distinct ikw values.
 *
 * @param cta
 * @param hl
 * @param co
 */
function setupBuildingFinalContinueUrlOnFormSubmit(cta, hl, co) {
    // If needed, we could emulate the mousedown event to perform the appendParamsOnce() functionality.
    // This shouldn't be required though, since the user already clicked on the CTA to open the modal.
    // const cta = document.querySelector(".smb-cta-btn.has-destination-url");
    // const clickEvent = document.createEvent('MouseEvents');
    // clickEvent.initEvent("mousedown", true, true);
    // cta.dispatchEvent(clickEvent);
    // cta.href: https://employers.indeed.com/p/post-job?hl=en&co=US -> https://employers.indeed.com/p/post-job?hl=en&co=US&from=resourcecenter_emp_info&isid=info_rolesandresponsibilitiestemplate&ikw=firstsectionpostjob

    // Grab the CTA's href if it exists, otherwise grab its child anchor's href if it exists, otherwise default to the job posting funnel.
    // The child anchor is only a thing in the DM Offer Video template.
    // The default value is "just in case", but it should never happen since our templates usually use the CTA shared components.
    const href = cta.href
        || document.querySelector("." + cta.className.replaceAll(" ", ".") + ">a")
        || "https://employers.indeed.com/p/post-job";
    const hrefComponents = parseHref(href);

    const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
    nopassModalWrapper.querySelector(".account-creation").addEventListener("submit", function () {
        const companyName = nopassModalWrapper.querySelector("#companyname").value;
        const baseContinueUrlWithTrackingParams = buildBaseContinueUrlWithTrackingParams(hrefComponents, hl, co);
        const finalContinueUrl = buildFinalContinueUrlWithJobPostingFunnelParamsIfNeeded(baseContinueUrlWithTrackingParams, companyName);
        nopassModalWrapper.querySelector('[name="postJobURL"]').value = finalContinueUrl; // The Allspark endpoint uses 'postJobURL' as the field name, but it's more accurate to think of it as the 'continue' URL for Passport.
    });
}

/**
 * Sets up the various behaviors required for the modal such as
 * modal open/close, company name autocomplete, building the 'continue' URL,
 * and creating an account using the Allspark /hireV2/account/preregister endpoint.
 */
function setupNopassModalBehaviorIfNeeded() {
    if (!isNewUser() || document.getElementById("nopass-modal-wrapper") === null) {
        console.debug(`Skipping NoPass setup.
        --> User is logged in: ${isUserLoggedIn()}
        --> Returning user: ${isReturningUser()}
        --> New user: ${isNewUser()}
        --> Modal wrapper is null at time of Javascript running: ${document.getElementById("nopass-modal-wrapper") === null}
        --> Document at time of Javascript running: `, document
        );
        return;
    }

    setupModalCloseListeners();
    setupModalFocusTrap();

    const locale = getLocale();
    const hl = locale.hl;
    const co = locale.co;
    updateLocalesInLinksAndAttributes(hl, co);

    setupCompanyNameAutocomplete(hl, co);
    setupEmailValidationListener();
    enableSubmitButtonIfInputsAreValid();

    // Hijack the behavior of every SMB CTA button on the page to open the modal
    // instead of redirecting to the destination.
    // This allows us to not have to worry about duplicating the CTA and its styles.
    // We use an additional class name to prevent this Javascript from affecting anything just using
    // the 'smb-cta-btn' and 'dm-offer-cta-btn' class names for just the styles.
    const ctas = document.querySelectorAll(".smb-cta-btn.has-destination-url, .dm-offer-cta-btn.has-destination-url");
    ctas.forEach(cta => cta.addEventListener("click", function (e) {
        e.preventDefault();
        const nopassModalWrapper = document.getElementById("nopass-modal-wrapper");
        nopassModalWrapper.classList.remove("hide-nopass");
        const companyName = nopassModalWrapper.querySelector("#companyname");
        companyName.focus();
        setupBuildingFinalContinueUrlOnFormSubmit(cta, hl, co);
    }));
}

window.addEventListener('load', setupNopassModalBehaviorIfNeeded);

// Post-Press already serves the below Javascript in hire.min.js,
// so this is here so that the behavior is observable on WordPress too.
// Define the functions only if they're not already present.
// We intentionally declare the functions on the window so
// 1) they're accessible outside of the if-blocks
// 2) we don't run into an 'already declared' issue when minifying and concatenating the JS

if (typeof appendParamsOnce !== 'function') {
    /**
     * Appends URL parameters to links on mousedown for best compatibility with Tophat environment
     *
     * We clone this into each template that uses indeed-ikw-isid-magic (all of them).
     * It's faster to copy it than to add a jsoup dependency or a separately built shared script.
     *
     * @param url
     * @param params
     * @returns {string|*}
     */
    window.appendParamsOnce = function (url, params) {
        const useParams = params.replace(/^(\?|\&)/, '');
        const anchorRegExp = new RegExp(/#.*$/);
        const urlAnchorMatch = url.match(anchorRegExp);

        const anchor = urlAnchorMatch == null ? '' : urlAnchorMatch;
        url = url.replace(anchorRegExp, '');

        if (url.match(new RegExp('[\\?|\\&]' + regExpEscape(useParams))) == null) {
            url += (url.indexOf('?') > 0 ? '&' : '?') + useParams;
        }
        url += anchor;

        return url;
    };
}

if (typeof regExpEscape !== 'function') {
    /** Used in appendParamsOnce */
    window.regExpEscape = function (s) {
        return String(s).replace(/([-()\[\]{}+?*.$\^|,:#<!\\])/g, '\\\$1').replace(/\x08/g, '\\x08');
    };
}
